import { useState, type FormEvent } from 'react';
import type { Editor } from '@tiptap/react';
import { FiLink } from 'react-icons/fi';
import { Button } from '@/shared/ui/button';
import { buttonStyles } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Popover, PopoverContent, PopoverTitle, PopoverTrigger } from '@/shared/ui/popover';
import { richTextToolbarStyles } from '@/shared/ui/rich-text-editor/styles';
import { toolbarIconClass } from './toolbar-controls';

/**
 * The link editor.
 *
 * A link is inserted or updated with `extendMarkRange` first, so applying a URL
 * with a word selected and the cursor inside the link both do what the operator
 * means. Emptying the field removes the link instead of writing an `href=""`.
 *
 * The trigger carries the state: "Add link" when there is no link at the cursor,
 * "Edit link" when there is, because a control that always says "Link" makes the
 * operator open it to find out which action they are about to take.
 */
export function LinkPopover({ editor, isDisabled }: { editor: Editor; isDisabled: boolean }) {
  const [open, setOpen] = useState(false);
  const [href, setHref] = useState('');
  const hasLink = editor.isActive('link');

  function handleOpenChange(next: boolean) {
    if (next) setHref(String(editor.getAttributes('link').href ?? ''));
    setOpen(next);
  }

  function apply(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const target = href.trim();
    const chain = editor.chain().focus().extendMarkRange('link');
    if (target) chain.setLink({ href: target }).run();
    else chain.unsetLink().run();
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        aria-label={hasLink ? 'Edit link' : 'Add link'}
        disabled={isDisabled}
        className={buttonStyles({ variant: 'ghost', size: 'xs' })}
      >
        <FiLink className={toolbarIconClass} aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent align="start">
        <PopoverTitle>Link</PopoverTitle>
        <form onSubmit={apply} className={richTextToolbarStyles.popoverForm}>
          <Input
            label="URL"
            value={href}
            placeholder="https://"
            onChange={(event) => setHref(event.target.value)}
          />
          <Button type="submit" size="sm">
            Apply
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  );
}
